import { BillingCycleType, CostType, DeviceCategory, QuotationOption, QuotationStatus } from "models/quotation";
import { currencyCode } from "utils/currencyCode";
import phoneCode from "utils/phoneCode";

export const QUOTATION_REGION_OPTIONS: Array<{ value: string; label: string }> = currencyCode.map((c) => ({
  value: c.region,
  label: `${c.countryName} (${c.region})`,
}));

export const getQuotationRegionOptions = (
  selectedRegion?: string
): Array<{ value: string; label: string }> => {
  const options = [...QUOTATION_REGION_OPTIONS];
  if (
    selectedRegion &&
    !options.some((opt) => opt.value.toUpperCase() === selectedRegion.toUpperCase())
  ) {
    const pc = phoneCode.find((p) => p.code.toUpperCase() === selectedRegion.toUpperCase());
    const label = pc ? `${pc.name} (${selectedRegion})` : selectedRegion;
    options.push({ value: selectedRegion, label });
  }
  return options;
};

export const getCurrencyForRegion = (region?: string): string => {
  if (!region) return "AED";
  const upper = region.toString().trim().toUpperCase();
  if (upper === "IN" || upper === "INDIA") return "INR";
  if (upper === "USA" || upper === "US" || upper === "UNITED STATES") return "USD";
  if (upper === "AE" || upper === "UAE" || upper === "UNITED ARAB EMIRATES") return "AED";
  if (upper === "KW" || upper === "KUWAIT") return "KWD";
  if (upper === "QA" || upper === "QATAR") return "QAR";
  if (upper === "OM" || upper === "OMAN") return "OMR";
  if (upper === "SA" || upper === "SAUDI ARABIA" || upper === "KSA") return "SAR";

  const direct = currencyCode.find(
    (c) => c.region.toUpperCase() === upper || c.countryName.toUpperCase() === upper
  );
  if (direct) {
    if (direct.currency_code === "Rs" || direct.region === "IN") return "INR";
    return direct.currency_code;
  }
  return "AED";
};

export const resolveCustomerRegion = (
  customer?: { country?: string; countryRegion?: string; region?: string } | null
): string => {
  if (!customer) return "AE";
  const raw = (customer.country || customer.countryRegion || customer.region || "").toString().trim();
  if (!raw) return "AE";

  const upper = raw.toUpperCase();
  const directMatch = currencyCode.find(
    (c) => c.region.toUpperCase() === upper || c.countryName.toUpperCase() === upper
  );
  if (directMatch) return directMatch.region;

  if (upper === "US") return "USA";

  const pc = phoneCode.find(
    (p) => p.code.toUpperCase() === upper || p.name.toUpperCase() === upper
  );
  if (pc) {
    const pcMatch = currencyCode.find(
      (c) => c.countryName.toUpperCase() === pc.name.toUpperCase() || c.region.toUpperCase() === pc.code.toUpperCase()
    );
    if (pcMatch) return pcMatch.region;
  }

  return upper;
};

export const ALL_QUOTATION_COLUMNS = [
  { key: "quotationNumber", label: "Quotation No." },
  { key: "title", label: "Title / Proposal" },
  { key: "customer", label: "Customer" },
  { key: "optionsCount", label: "Options" },
  { key: "grandTotal", label: "Amount" },
  { key: "validUntil", label: "Valid Until" },
  { key: "status", label: "Status" },
  { key: "action", label: "Action" },
];

export const DEFAULT_QUOTATION_COLUMNS = [
  "quotationNumber",
  "customer",
  "optionsCount",
  "grandTotal",
  "validUntil",
  "status",
  "action",
];

export const DEVICE_CATEGORY_OPTIONS: Array<{ value: DeviceCategory; label: string }> = [
  { value: "POS_TERMINAL", label: "POS Terminal" },
  { value: "PRINTER", label: "Receipt Printer" },
  { value: "BARCODE_SCANNER", label: "Barcode Scanner" },
  { value: "KIOSK", label: "Self-Service Kiosk" },
  { value: "TABLET", label: "Tablet / Handheld" },
  { value: "OTHER", label: "Other Hardware / Accessory" },
];

export const COST_TYPE_OPTIONS: Array<{ value: CostType; label: string }> = [
  { value: "ONE_TIME", label: "ONE TIME" },
  { value: "RENEWAL", label: "RENEWAL" },
];

export const BILLING_CYCLE_OPTIONS = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "ANNUAL", label: "Annual (12 Months)" },
  { value: "CUSTOM", label: "Custom Billing Cycle" },
  { value: "ONE_TIME", label: "One-Time" },
];

export const resolvePlanBillingCycle = (plan: any): BillingCycleType => {
  if (!plan) return "MONTHLY";
  const rawCycle = (plan.billingCycle || "").toString().trim().toUpperCase();
  if (rawCycle === "MONTHLY" || rawCycle === "MONTH") return "MONTHLY";
  if (rawCycle === "ANNUAL" || rawCycle === "YEARLY" || rawCycle === "ANNUALLY") return "ANNUAL";
  if (rawCycle === "ONE_TIME" || rawCycle === "ONETIME") return "ONE_TIME";
  if (rawCycle === "CUSTOM" || rawCycle === "QUARTERLY" || rawCycle === "HALF_YEARLY") return "CUSTOM";

  if (plan.durationInMonths) {
    const months = Number(plan.durationInMonths);
    if (months === 1) return "MONTHLY";
    if (months === 12) return "ANNUAL";
    return "CUSTOM";
  }

  const nameUpper = (plan.planName || "").toUpperCase();
  if (nameUpper.includes("MONT") || nameUpper.includes("MONTH")) return "MONTHLY";
  if (nameUpper.includes("ANNUAL") || nameUpper.includes("YEAR")) return "ANNUAL";

  return "MONTHLY";
};

export const DISCOUNT_TYPE_OPTIONS = [
  { value: "FIXED", label: "Fixed Amount" },
  { value: "PERCENTAGE", label: "Percentage (%)" },
];

export const QUOTATION_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Status (All)" },
  { value: "DRAFT", label: "Draft" },
  { value: "SENT", label: "Sent / Pending" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" },
];

export const QUOTATION_STATUS_PILL_OPTIONS: Array<{ val: string; label: string }> = [
  { val: "", label: "All" },
  { val: "DRAFT", label: "Draft" },
  { val: "SENT", label: "Sent" },
  { val: "ACCEPTED", label: "Accepted" },
  { val: "REJECTED", label: "Rejected" },
  { val: "CANCELLED", label: "Cancelled" },
  { val: "EXPIRED", label: "Expired" },
];

export const getQuotationStatusBadgeClass = (status?: QuotationStatus | string): string => {
  switch (status?.toUpperCase()) {
    case "DRAFT":
      return "quotation-badge quotation-badge--draft";
    case "ACCEPTED":
      return "quotation-badge quotation-badge--accepted";
    case "SENT":
      return "quotation-badge quotation-badge--sent";
    case "REJECTED":
      return "quotation-badge quotation-badge--rejected";
    case "CANCELLED":
      return "quotation-badge quotation-badge--cancelled";
    case "EXPIRED":
      return "quotation-badge quotation-badge--expired";
    default:
      return "quotation-badge quotation-badge--default";
  }
};

/**
 * Accurately calculate the pricing breakdown, dual pricing (initial vs recurring) and grand total for a single quotation option
 * factoring in ONE_TIME vs RENEWAL cost classification per line item, device, and service.
 */
export const calculateOptionPricing = (option: Partial<QuotationOption>) => {
  const softwareSubtotal = Number(option.pricingSummary?.softwareSubtotal) || 0;

  // Devices breakdown by costType
  let oneTimeHardware = 0;
  let renewalHardware = 0;
  (option.devices || []).forEach((item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unitPrice) || 0;
    const cost = qty * price;
    if (item.costType === "RENEWAL") {
      renewalHardware += cost;
    } else {
      oneTimeHardware += cost;
    }
  });

  // Services breakdown by costType
  let oneTimeServices = 0;
  let renewalServices = 0;
  (option.customSystems || []).forEach((item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unitPrice) || 0;
    const cost = qty * price;
    if (item.costType === "RENEWAL") {
      renewalServices += cost;
    } else {
      oneTimeServices += cost;
    }
  });

  // Items breakdown by costType
  let oneTimeItems = 0;
  let renewalItems = 0;
  (option.items || []).forEach((item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unitPrice) || 0;
    const cost = qty * price;
    if (item.costType === "ONE_TIME") {
      oneTimeItems += cost;
    } else {
      renewalItems += cost;
    }
  });

  const hardwareSubtotal = oneTimeHardware + renewalHardware;
  const servicesSubtotal = oneTimeServices + renewalServices;
  const itemsSubtotal = oneTimeItems + renewalItems;

  const oneTimeSubtotal = oneTimeHardware + oneTimeServices + oneTimeItems;
  const renewalSubtotal = softwareSubtotal + renewalHardware + renewalServices + renewalItems;

  const rawSubtotal = oneTimeSubtotal + renewalSubtotal;

  const discountType = option.pricingSummary?.discount?.type || "FIXED";
  const discountVal = Number(option.pricingSummary?.discount?.value) || 0;

  let discountAmount = 0;
  if (discountType === "PERCENTAGE") {
    discountAmount = (rawSubtotal * Math.min(100, Math.max(0, discountVal))) / 100;
  } else {
    discountAmount = Math.min(rawSubtotal, Math.max(0, discountVal));
  }

  const taxableAmount = Math.max(0, rawSubtotal - discountAmount);

  // Tax / VAT calculation: only apply if explicitly provided in option
  let taxRates = 0;
  if (Array.isArray(option.pricingSummary?.taxes) && option.pricingSummary.taxes.length > 0) {
    taxRates = option.pricingSummary.taxes.reduce((sum, t) => sum + (Number(t.rate) || 0), 0);
  } else if (typeof option.pricingSummary?.taxPercentage === "number") {
    taxRates = option.pricingSummary.taxPercentage;
  } else {
    taxRates = 0;
  }

  const taxAmount = Math.round(((taxableAmount * taxRates) / 100) * 100) / 100;
  const grandTotal = Math.round((taxableAmount + taxAmount) * 100) / 100;
  const grandTotalInitial = grandTotal;
  const recurringPayment = renewalSubtotal;

  return {
    softwareSubtotal,
    hardwareSubtotal,
    servicesSubtotal,
    itemsSubtotal,
    oneTimeSubtotal,
    renewalSubtotal,
    recurringPayment,
    rawSubtotal,
    discountAmount,
    taxableAmount,
    taxRates,
    taxAmount,
    grandTotal,
    grandTotalInitial,
    currency: option.pricingSummary?.currency || getCurrencyForRegion(option.region),
  };
};

export const FEATURE_PRESETS = [
  "Multi-location Support",
  "Inventory Management",
  "Real-time Reports",
  "24/7 Priority Support",
  "Self-Service Kiosk",
  "Kitchen Display System (KDS)",
  "Customer Loyalty & Rewards",
  "Accounting ERP Sync",
];
